import React, { useState, useEffect } from 'react';
import { MapPin, Car, ShowerHead, ArrowRight, Phone, Navigation, ParkingCircle } from 'lucide-react';
import { Button } from './ui/button';
import { locations as fallbackLocations, businessInfo } from '../data/mock';
import BookingCalendar from './BookingCalendar';
import axios from 'axios';
import { API } from '../config';
import { trackPixel } from '../lib/pixel';

const LocationsSection = () => {
  const [locations, setLocations] = useState(fallbackLocations);
  const [bookingLocation, setBookingLocation] = useState(null);

  useEffect(() => {
    axios.get(`${API}/locations`)
      .then(res => { if (res.data?.length) setLocations(res.data); })
      .catch(() => {});
  }, []);
  return (
    <section id="locations" className="py-28 bg-bone relative overflow-hidden">
      {/* Decorative Background */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-sage-50/30 to-transparent" />
      
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-luxury-gold text-xs uppercase tracking-[0.3em] font-medium">
            Visit Us
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-luxury-green mt-4 mb-6">
            Two Convenient
            <span className="block italic text-luxury-green/70 mt-1">Dublin Locations</span>
          </h2>
          <p className="text-stone-500 font-light">
            Choose the location that suits you best.
          </p>
        </div>

        {/* Locations Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {locations.map((location) => (
            <div
              key={location.id}
              className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-500 group border border-stone-100 hover:border-luxury-gold/20"
            >
              {/* Map Image from Screenshot */}
              <div className="h-64 relative overflow-hidden">
                <a 
                  href={location.mapUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block w-full h-full"
                >
                  {location.id === 1 ? (
                    <img 
                      src="https://customer-assets.emergentagent.com/job_905f2ba3-e3d2-46ba-8c1f-ae54c31b9b64/artifacts/b7g1cw26_Screenshot_20260113_224330_Maps.jpg"
                      alt="City Centre Location Map"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <img 
                      src="https://customer-assets.emergentagent.com/job_905f2ba3-e3d2-46ba-8c1f-ae54c31b9b64/artifacts/whmz2m8q_Screenshot_20260113_224550_Maps.jpg"
                      alt="Clarehall Location Map"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  )}
                  <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <span className="bg-white text-stone-800 px-4 py-2 rounded-full font-medium shadow-lg">
                      Click to open in Google Maps
                    </span>
                  </div>
                </a>
                {/* Location Badge */}
                <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg z-10">
                  <span className="font-semibold text-stone-800">{location.name}</span>
                </div>
              </div>

              {/* Location Details */}
              <div className="p-8">
                <h3 className="text-2xl font-semibold text-stone-800 mb-1">
                  {location.name} Location
                </h3>
                <p className="text-stone-500 mb-2">{location.address}</p>
                
                {/* Landmark Highlight */}
                {location.landmark && (
                  <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-800 px-3 py-1.5 rounded-full text-sm font-medium mb-4">
                    <MapPin size={14} />
                    {location.landmark}
                  </div>
                )}

                {/* Features */}
                <div className="space-y-2 mb-6">
                  {location.features.map((feature) => (
                    <div key={feature} className="flex items-center gap-3 text-stone-600">
                      <div className="w-2 h-2 bg-sage-500 rounded-full" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Nearby Parking for City Centre */}
                {location.nearbyParking && location.nearbyParking.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                    <div className="flex items-center gap-2 text-blue-800 font-medium mb-2">
                      <ParkingCircle size={18} />
                      <span>Nearest Parking</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {location.nearbyParking.map((parking) => (
                        <span key={parking} className="bg-white text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                          {parking}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Amenities */}
                <div className="flex gap-4 mb-8">
                  <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${location.parking ? 'bg-sage-50 text-sage-700' : 'bg-stone-100 text-stone-400'}`}>
                    <Car size={18} />
                    <span className="text-sm font-medium">
                      {location.parking ? 'Free Parking' : 'No Dedicated Parking'}
                    </span>
                  </div>
                  <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${location.shower ? 'bg-sage-50 text-sage-700' : 'bg-stone-100 text-stone-400'}`}>
                    <ShowerHead size={18} />
                    <span className="text-sm font-medium">
                      {location.shower ? 'Shower' : 'No Shower'}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={() => setBookingLocation(location.name?.toLowerCase().includes('clare') ? 'clarehall' : 'city_centre')}
                    className="flex-1 bg-sage-600 hover:bg-sage-700 text-white rounded-full transition-all duration-300 group/btn"
                    data-testid={`book-location-btn-${location.id}`}
                  >
                    Book This Location
                    <ArrowRight size={16} className="ml-2 group-hover/btn:translate-x-1 transition-transform" />
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => window.open(location.mapUrl, '_blank')}
                    className="border-stone-300 text-stone-600 hover:border-sage-600 hover:text-sage-600 rounded-full"
                  >
                    <Navigation size={16} className="mr-2" />
                    Get Directions
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Landmarks Info */}
        <div className="mt-12 bg-white rounded-2xl p-6 shadow-lg max-w-4xl mx-auto">
          <h4 className="font-semibold text-stone-800 mb-4 text-center text-lg">How to Find Us</h4>
          <div className="grid sm:grid-cols-2 gap-6 text-sm">
            <div className="bg-gradient-to-br from-stone-50 to-sage-50/30 rounded-xl p-5">
              <p className="font-semibold text-sage-700 mb-3 text-base">City Centre (D1):</p>
              <ul className="space-y-2 text-stone-600">
                <li className="flex items-start gap-2">
                  <MapPin size={16} className="text-sage-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Opposite The Celt Pub</strong> on Talbot Street</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-4 h-4 flex-shrink-0"></span>
                  <span>2 min walk from Connolly Train Station</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-4 h-4 flex-shrink-0"></span>
                  <span>3 min walk from Busáras Bus Station</span>
                </li>
                <li className="flex items-start gap-2">
                  <ParkingCircle size={16} className="text-blue-500 mt-0.5 flex-shrink-0" />
                  <span>Parking: <strong>Irish Life Car Park</strong> or <strong>Q-Park</strong></span>
                </li>
              </ul>
            </div>
            <div className="bg-gradient-to-br from-stone-50 to-warm-50/30 rounded-xl p-5">
              <p className="font-semibold text-sage-700 mb-3 text-base">Clarehall (D17):</p>
              <ul className="space-y-2 text-stone-600">
                <li className="flex items-start gap-2">
                  <MapPin size={16} className="text-sage-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Clare Village Apartments</strong>, Donaghmede</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-4 h-4 flex-shrink-0"></span>
                  <span>Near Clarehall Shopping Centre</span>
                </li>
                <li className="flex items-start gap-2">
                  <Car size={16} className="text-sage-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Free dedicated car park</strong> on-site</span>
                </li>
                <li className="flex items-start gap-2">
                  <ShowerHead size={16} className="text-sage-500 mt-0.5 flex-shrink-0" />
                  <span>Shower facilities available</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-4 bg-white px-8 py-4 rounded-2xl shadow-lg">
            <Phone size={24} className="text-sage-600" />
            <div className="text-left">
              <p className="text-sm text-stone-500">Need help finding us? Call</p>
              <a href={`tel:${businessInfo.phone}`} onClick={() => trackPixel('Contact', { content_name: 'Locations Phone', method: 'phone' })} className="text-xl font-semibold text-stone-800 hover:text-sage-600 transition-colors">
                {businessInfo.phone}
              </a>
            </div>
          </div>
        </div>
      </div>
      <BookingCalendar
        isOpen={!!bookingLocation}
        onClose={() => setBookingLocation(null)}
        initialLocation={bookingLocation}
      />
    </section>
  );
};

export default LocationsSection;
